import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Items() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = () => ({ name: "", sku: "", hsn: "", unit: "PCS", gst_rate: 18, stock: 0, description: "" });
  const [form, setForm] = useState(empty());
  const [q, setQ] = useState("");

  const load = async () => setItems((await api.get("/items")).data);
  useEffect(() => { load(); }, []);

  const validateHsn = (v) => /^\d{8}$/.test(String(v || "").trim());

  const save = async () => {
    if (!form.name.trim()) return toast.error("Part description is required");
    if (!validateHsn(form.hsn)) return toast.error("HSN code must be exactly 8 digits");
    try {
      if (editing) await api.put(`/items/${editing.item_id}`, form);
      else await api.post("/items", form);
      toast.success(editing ? "Part updated" : "Part added");
      setOpen(false); setEditing(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this part?")) return;
    await api.delete(`/items/${id}`); toast.success("Deleted"); load();
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty(), ...it }); setOpen(true); };

  const filtered = q
    ? items.filter((i) =>
        [i.name, i.sku, i.hsn, i.description].some((v) =>
          String(v || "").toLowerCase().includes(q.toLowerCase())
        ))
    : items;

  return (
    <div>
      <PageHeader
        title="Parts Master"
        subtitle="Part number, description, HSN & GST — the single source that flows into price lists, POs and invoices"
        action={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty()); } }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Part
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Part" : "New Part"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                <F label="Part Number">
                  <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="e.g. TSI-BOLT-M12" data-testid="item-sku" />
                </F>
                <F label="HSN Code (8-digit)">
                  <Input
                    value={form.hsn}
                    onChange={(e) => setForm({ ...form, hsn: e.target.value.replace(/[^\d]/g, "").slice(0, 8) })}
                    placeholder="e.g. 73181500"
                    maxLength={8}
                    inputMode="numeric"
                    data-testid="item-hsn"
                    className={form.hsn && !validateHsn(form.hsn) ? "border-rose-400" : ""}
                  />
                  {form.hsn && !validateHsn(form.hsn) && (
                    <div className="text-xs text-rose-600 mt-1">HSN must be exactly 8 digits</div>
                  )}
                </F>
                <div className="md:col-span-2">
                  <F label="Part Description">
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Hex Bolt M12x40 Stainless Steel" data-testid="item-name" />
                  </F>
                </div>
                <F label="Unit"><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="PCS, KG, LT..." /></F>
                <F label="GST Rate (%)"><Input type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: +e.target.value })} /></F>
                <F label="Opening Stock"><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: +e.target.value })} /></F>
                <div className="md:col-span-2">
                  <F label="Notes (optional)">
                    <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </F>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-item-btn">
                  {editing ? "Save changes" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-8 py-6 space-y-4">
        <div className="max-w-sm">
          <Input placeholder="Search part number, name or HSN…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Card className="border-slate-200 overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Part No.</TableHead>
                <TableHead>Part Description</TableHead>
                <TableHead>HSN Code</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">GST%</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">
                  {items.length === 0 ? "No parts yet. Click Add Part to begin." : "No parts match your search."}
                </TableCell></TableRow>
              )}
              {filtered.map((i) => (
                <TableRow key={i.item_id}>
                  <TableCell className="font-mono text-xs">{i.sku || "—"}</TableCell>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell className="font-mono text-xs">{i.hsn || "—"}</TableCell>
                  <TableCell className="text-slate-500">{i.unit}</TableCell>
                  <TableCell className="text-right number-cell">{i.gst_rate}%</TableCell>
                  <TableCell className={`text-right number-cell ${i.stock <= 0 ? "text-rose-600" : i.stock <= 5 ? "text-amber-600" : ""}`}>{i.stock}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(i)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(i.item_id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>; }
